import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../api/axiosInstance';
import { getApiErrorMessage } from '../../utils/apiErrors';
import swal from '../../utils/swal';
import Loader from '../../components/loading/Loader';
import './Sedes.css';

function Sedes() {
    const { user } = useAuth();
    const medicoId = user?.medicoId;

    const [sedesAsignadas, setSedesAsignadas] = useState([]);
    const [sedesIdsDelMedico, setSedesIdsDelMedico] = useState([]);
    const [availableSedes, setAvailableSedes] = useState([]);
    const [availableTotal, setAvailableTotal] = useState(0);
    const [availablePage, setAvailablePage] = useState(1);
    const [isMobile, setIsMobile] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const pageSize = isMobile ? 1 : 4;
    const [loading, setLoading] = useState(true);
    const [accionEnCurso, setAccionEnCurso] = useState(null);

    const idsAsignados = new Set(sedesIdsDelMedico.map(String));
    const misSedes = sedesAsignadas;

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 640px)');
        const handleResize = (event) => {
            setIsMobile(event.matches);
        };

        setIsMobile(mediaQuery.matches);
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleResize);
        } else {
            mediaQuery.addListener(handleResize);
        }

        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handleResize);
            } else {
                mediaQuery.removeListener(handleResize);
            }
        };
    }, []);

    useEffect(() => {
        setAvailablePage(1);
    }, [isMobile]);

    const cargarDatos = useCallback(async (page = 1, search = searchQuery) => {
        if (!medicoId) return;

        setLoading(true);
        try {
            const medicoRes = await axiosInstance.get(`/medicos/${medicoId}`);
            const sedesIds = medicoRes.data.sedesIds ?? [];
            setSedesIdsDelMedico(sedesIds);

            const assignedPromise = sedesIds.length > 0
                ? axiosInstance.get(`/sedes?ids=${sedesIds.join(',')}`)
                : Promise.resolve({ data: [] });

            const queryParams = new URLSearchParams();
            queryParams.set('page', page);
            queryParams.set('pageSize', pageSize);
            if (search.trim()) queryParams.set('search', search.trim());
            if (sedesIds.length > 0) {
                queryParams.set('excludeIds', sedesIds.join(','));
            }

            const availablePromise = axiosInstance.get(`/sedes?${queryParams.toString()}`);

            const [assignedRes, availableRes] = await Promise.all([assignedPromise, availablePromise]);

            setSedesAsignadas(assignedRes.data ?? []);
            setAvailableSedes(availableRes.data.items ?? []);
            setAvailableTotal(availableRes.data.total ?? 0);
            setAvailablePage(availableRes.data.page ?? page);

        } catch (err) {
            const mensaje = getApiErrorMessage(
                err,
                'No se pudieron cargar las sedes. Intentá de nuevo.'
            );
            await swal.fire({ icon: 'error', title: 'Error', text: mensaje, confirmButtonColor: '#db1414' });
        } finally {
            setLoading(false);
        }
    }, [medicoId, searchQuery, pageSize]);

    useEffect(() => {
        cargarDatos(availablePage, searchQuery);
    }, [cargarDatos, availablePage, searchQuery]);

    useEffect(() => {
        const maxPage = Math.max(1, Math.ceil(availableTotal / pageSize));
        if (availablePage > maxPage) {
            setAvailablePage(1);
        }
    }, [availablePage, availableTotal, pageSize]);

    const handleBuscar = async (event) => {
        event.preventDefault();
        setSearchQuery(searchText);
        setAvailablePage(1);
    };

    const handleSearchTextChange = (event) => {
        setSearchText(event.target.value);
    };

    const handlePageChange = (newPage) => {
        const pageCount = Math.max(1, Math.ceil(availableTotal / pageSize));
        const nextPage = Math.max(1, Math.min(newPage, pageCount));
        setAvailablePage(nextPage);
    };

    async function handleAgregar(sedeId) {
        setAccionEnCurso(sedeId);
        try {
            const { data } = await axiosInstance.post(
                `/medicos/${medicoId}/sedes/${sedeId}`
            );
            setSedesIdsDelMedico(data.sedesIds ?? []);
            // refresh lists
            await cargarDatos(availablePage, searchQuery);
        } catch (err) {
            const mensaje = getApiErrorMessage(
                err,
                'No se pudo agregar la sede. Intentá de nuevo.'
            );
            await swal.fire({ icon: 'error', title: 'Error', text: mensaje, confirmButtonColor: '#db1414' });
        } finally {
            setAccionEnCurso(null);
        }
    }

    async function handleEliminar(sedeId) {
        const confirm = await swal.fire({
            title: '¿Eliminar sede?',
            text: 'La sede dejará de estar disponible para tus servicios.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#db1414',
        });
        if (!confirm.isConfirmed) return;

        setAccionEnCurso(sedeId);
        try {
            const { data } = await axiosInstance.delete(
                `/medicos/${medicoId}/sedes/${sedeId}`
            );
            setSedesIdsDelMedico(data.sedesIds ?? []);
            // refresh lists
            await cargarDatos(availablePage, searchQuery);
        } catch (err) {
            const mensaje = getApiErrorMessage(
                err,
                'No se pudo eliminar la sede. Intentá de nuevo.'
            );
            await swal.fire({ icon: 'error', title: 'Error', text: mensaje, confirmButtonColor: '#db1414' });
        } finally {
            setAccionEnCurso(null);
        }
    }

    if (!medicoId) {
        return (
            <p className="sedes__mensaje">
                No se encontró el perfil de médico. Cerrá sesión e ingresá nuevamente.
            </p>
        );
    }

    if (loading) {
        return <Loader mensaje="Cargando sedes..." />;
    }

    const totalPages = Math.max(1, Math.ceil(availableTotal / pageSize));

    return (
        <div className="sedes">
            <section className="sedes__section">
                <header className="sedes__section-header">MIS SEDES</header>
                <div className="sedes__grid">
                    {misSedes.length === 0 && (
                        <p className="sedes__vacio">Todavía no agregaste ninguna sede.</p>
                    )}
                    {misSedes.map(sede => (
                        <article key={sede._id} className="sedes__card">
                            <p className="sedes__card-nombre">{sede.nombre}</p>
                            <p className="sedes__card-direccion">
                                Dirección: {sede.direccion}
                            </p>
                            <button
                                type="button"
                                className="sedes__btn sedes__btn--danger"
                                disabled={accionEnCurso === sede._id}
                                onClick={() => handleEliminar(sede._id)}
                            >
                                {accionEnCurso === sede._id ? <Loader inline /> : 'Eliminar'}
                            </button>
                        </article>
                    ))}
                </div>
            </section>

            <section className="sedes__section">
                <header className="sedes__section-header">SEDES</header>
                <form className="sedes__search-form" onSubmit={handleBuscar}>
                    <input
                        type="text"
                        className="sedes__search-input"
                        placeholder="Buscar sede"
                        value={searchText}
                        onChange={handleSearchTextChange}
                    />
                    <button type="submit" className="sedes__btn sedes__btn--search">Buscar</button>
                </form>
                <div className="sedes__carousel">
                    {availableSedes.length === 0 && (
                        <p className="sedes__vacio">
                            {availableTotal === 0
                                ? 'No hay sedes registradas en el sistema.'
                                : 'No hay sedes en esta página. Cambiá de página para ver más resultados.'}
                        </p>
                    )}

                    <div className="sedes__carousel-inner">
                        <button
                            type="button"
                            className="sedes__carousel-arrow sedes__carousel-arrow--left"
                            disabled={availablePage <= 1}
                            onClick={() => handlePageChange(availablePage - 1)}
                        >
                            ‹
                        </button>
                        <div className="sedes__carousel-window">
                            <div className="sedes__carousel-track">
                                {availableSedes.map(sede => {
                                    const asignada = idsAsignados.has(String(sede._id));
                                    return (
                                        <article key={sede._id} className="sedes__card sedes__card--carousel">
                                            <p className="sedes__card-nombre">{sede.nombre}</p>
                                            <p className="sedes__card-direccion">Dirección: {sede.direccion}</p>
                                            <button
                                                type="button"
                                                className={`sedes__btn ${asignada ? 'sedes__btn--disabled' : ''}`}
                                                disabled={asignada || accionEnCurso === sede._id}
                                                onClick={() => handleAgregar(sede._id)}
                                            >
                                                {asignada
                                                    ? 'Agregada'
                                                    : accionEnCurso === sede._id
                                                        ? <Loader inline />
                                                        : 'Agregar'}
                                            </button>
                                        </article>
                                    );
                                })}
                            </div>
                        </div>
                        <button
                            type="button"
                            className="sedes__carousel-arrow sedes__carousel-arrow--right"
                            disabled={availablePage >= totalPages}
                            onClick={() => handlePageChange(availablePage + 1)}
                        >
                            ›
                        </button>
                    </div>

                    <div className="sedes__carousel-controls">
                        <span>Página {availablePage} de {totalPages}</span>
                        <span>{availableTotal} sede(s) encontradas</span>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default Sedes;
